import React, { useState, useEffect } from 'react';
import {
  Document, Page, View, Text, StyleSheet, Image, pdf
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import logoUrl from '../assets/logo.png';

const BK = '#000000';
const BORDER = '#000000';

const s = StyleSheet.create({
  page: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40, 
    fontFamily: 'Helvetica',
    fontSize: 7,
    color: BK,
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: BK,
    paddingBottom: 5,
  },
  logo: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  headerTextContainer: {
    flex: 1,
    marginLeft: 12,
  },
  title: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
  subtitle: {
    fontSize: 8,
    marginTop: 1,
    color: '#444',
  },
  cabecalhoSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
    padding: 5,
  },
  cabecalhoItem: {
    width: '25%',
    marginBottom: 6,
  },
  cabecalhoLabel: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    color: '#666',
    textTransform: 'uppercase',
  },
  cabecalhoValue: {
    fontSize: 8,
    marginTop: 1,
  },
  table: {
    width: '100%',
    borderWidth: 0.5,
    borderColor: BORDER,
    marginTop: 20, // Aproximadamente 1cm do cabeçalho
    marginBottom: 10,
  },
  tableHeaderGroup: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER,
  },
  tableHeaderSub: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#eee',
    minHeight: 15,
    alignItems: 'center',
  },
  tableRowTotal: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    fontFamily: 'Helvetica-Bold',
    minHeight: 18,
    alignItems: 'center',
  },
  cell: {
    paddingHorizontal: 2,
    textAlign: 'center',
    borderRightWidth: 0.5,
    borderRightColor: '#ccc',
    justifyContent: 'center',
  },
  cellLast: {
    borderRightWidth: 0,
  },
  cellText: {
    fontSize: 6,
  },
  cellTextBold: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
  },
  colData: { width: '6%' },
  colLoc: { width: '12%' },
  colConc: { width: '4%' },
  colNum: { width: '4.5%' }, 
  colSmall: { width: '4%' }, 
  
  rodapeSection: {
    marginTop: 'auto', 
    paddingHorizontal: 5,
    paddingBottom: 10,
  },
  rodapeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  rodapeItem: {
    width: '30%',
  },
  assinaturaLine: {
    marginTop: 15,
    borderTopWidth: 0.5,
    borderTopColor: BK,
    textAlign: 'center',
    paddingTop: 2,
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 6,
    color: '#999',
  }
});

const urlParaBase64 = (url) => {
  if (!url || typeof url !== 'string' || url.startsWith('data:')) return Promise.resolve(url);
  return new Promise((resolve) => {
    const el = document.createElement('img');
    el.crossOrigin = 'anonymous';
    el.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = el.naturalWidth;
        canvas.height = el.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(el, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) { resolve(null); }
    };
    el.onerror = () => resolve(null);
    el.src = url;
  });
};

export const FormularioBorrifacaoPDFDocument = ({ data, cabecalho, rodape, totalRow, logoBase64 }) => {
  const linhas = data || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return dateStr;
    } catch (e) { return dateStr; }
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={s.page}>
        <View style={s.header}>
          <Image src={logoBase64 || logoUrl} style={s.logo} />
          <View style={s.headerTextContainer}>
            <Text style={s.title}>RESUMO SEMANAL DE BORRIFAÇÃO</Text>
            <Text style={s.subtitle}>Vigilância Entomológica e Controle de Vetores</Text>
          </View>
        </View>

        <View style={s.cabecalhoSection}>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Dist. Sanitário</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.dist_sanit || '-'}</Text>
          </View>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Sub-Distrito</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.sub_distrito || '-'}</Text>
          </View>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Fase de Trabalho</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.fase_trabalho || '-'}</Text>
          </View>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Município</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.municipio || '-'}</Text>
          </View>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Semana</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.semana || '-'}</Text>
          </View>
          <View style={s.cabecalhoItem}>
            <Text style={s.cabecalhoLabel}>Turma</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.turma || '-'}</Text>
          </View>
          <View style={[s.cabecalhoItem, { width: '50%' }]}>
            <Text style={s.cabecalhoLabel}>Nome Guarda Chefe</Text>
            <Text style={s.cabecalhoValue}>{cabecalho.guarda_chefe || '-'}</Text>
          </View>
        </View>

        <View style={s.table}>
          <View style={s.tableHeaderGroup}>
            <View style={[s.cell, s.colData, { height: 20 }]}><Text style={s.cellTextBold}>Data</Text></View>
            <View style={[s.cell, s.colLoc]}><Text style={s.cellTextBold}>Localidade</Text></View>
            <View style={[s.cell, s.colConc]}><Text style={s.cellTextBold}>Conc.</Text></View>
            <View style={[s.cell, { width: '13.5%' }]}><Text style={s.cellTextBold}>Casas Trabalhadas</Text></View>
            <View style={[s.cell, { width: '13.5%' }]}><Text style={s.cellTextBold}>Casas Pendentes</Text></View>
            <View style={[s.cell, { width: '13.5%' }]}><Text style={s.cellTextBold}>Anexos Trab.</Text></View>
            <View style={[s.cell, { width: '13.5%' }]}><Text style={s.cellTextBold}>Unid. Domiciliares</Text></View>
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>Hab. Prot.</Text></View>
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>Ins. (cps)</Text></View>
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>Nº Triat.</Text></View>
            <View style={[s.cell, s.colSmall, s.cellLast]}><Text style={s.cellTextBold}>HD</Text></View>
          </View>

          <View style={s.tableHeaderSub}>
            <View style={[s.cell, s.colData]} />
            <View style={[s.cell, s.colLoc]} />
            <View style={[s.cell, s.colConc]} />
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Pos</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Neg</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#e2e8f0' }]}><Text style={s.cellTextBold}>Tot</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Fech</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Rec</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#e2e8f0' }]}><Text style={s.cellTextBold}>Tot</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Pos</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Neg</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#e2e8f0' }]}><Text style={s.cellTextBold}>Tot</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Pos</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellText}>Neg</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#e2e8f0' }]}><Text style={s.cellTextBold}>Tot</Text></View>
            
            <View style={[s.cell, s.colSmall]} />
            <View style={[s.cell, s.colSmall]} />
            <View style={[s.cell, s.colSmall]} />
            <View style={[s.cell, s.colSmall, s.cellLast]} />
          </View>

          {linhas.filter(l => l.localidade || l.data).map((l, i) => (
            <View key={i} style={s.tableRow}>
              <View style={[s.cell, s.colData]}><Text style={s.cellText}>{formatDate(l.data)}</Text></View>
              <View style={[s.cell, s.colLoc, { textAlign: 'left', paddingLeft: 4 }]}><Text style={s.cellText}>{l.localidade || '-'}</Text></View>
              <View style={[s.cell, s.colConc]}><Text style={s.cellText}>{l.concluida || '-'}</Text></View>
              
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.ct_pos || '0'}</Text></View>
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.ct_neg || '0'}</Text></View>
              <View style={[s.cell, s.colNum, { backgroundColor: '#f8fafc' }]}><Text style={s.cellTextBold}>{l.ct_total || '0'}</Text></View>
              
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.cp_fech || '0'}</Text></View>
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.cp_rec || '0'}</Text></View>
              <View style={[s.cell, s.colNum, { backgroundColor: '#f8fafc' }]}><Text style={s.cellTextBold}>{l.cp_total || '0'}</Text></View>
              
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.at_pos || '0'}</Text></View>
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.at_neg || '0'}</Text></View>
              <View style={[s.cell, s.colNum, { backgroundColor: '#f8fafc' }]}><Text style={s.cellTextBold}>{l.at_total || '0'}</Text></View>
              
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.ud_pos || '0'}</Text></View>
              <View style={[s.cell, s.colNum]}><Text style={s.cellText}>{l.ud_neg || '0'}</Text></View>
              <View style={[s.cell, s.colNum, { backgroundColor: '#f8fafc' }]}><Text style={s.cellTextBold}>{l.ud_total || '0'}</Text></View>
              
              <View style={[s.cell, s.colSmall]}><Text style={s.cellText}>{l.hab_proteg || '0'}</Text></View>
              <View style={[s.cell, s.colSmall]}><Text style={s.cellText}>{l.consumo_inset || '0'}</Text></View>
              <View style={[s.cell, s.colSmall]}><Text style={s.cellText}>{l.n_triat || '0'}</Text></View>
              <View style={[s.cell, s.colSmall, s.cellLast]}><Text style={s.cellText}>{l.hd || '-'}</Text></View>
            </View>
          ))}

          <View style={s.tableRowTotal}>
            <View style={[s.cell, { width: '22%', textAlign: 'right', paddingRight: 10 }]}><Text style={s.cellTextBold}>TOTAL GERAL</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.ct_pos || '0'}</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.ct_neg || '0'}</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#cbd5e1' }]}><Text style={s.cellTextBold}>{totalRow.ct_total || '0'}</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.cp_fech || '0'}</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.cp_rec || '0'}</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#cbd5e1' }]}><Text style={s.cellTextBold}>{totalRow.cp_total || '0'}</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.at_pos || '0'}</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.at_neg || '0'}</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#cbd5e1' }]}><Text style={s.cellTextBold}>{totalRow.at_total || '0'}</Text></View>
            
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.ud_pos || '0'}</Text></View>
            <View style={[s.cell, s.colNum]}><Text style={s.cellTextBold}>{totalRow.ud_neg || '0'}</Text></View>
            <View style={[s.cell, s.colNum, { backgroundColor: '#cbd5e1' }]}><Text style={s.cellTextBold}>{totalRow.ud_total || '0'}</Text></View>
            
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>{totalRow.hab_proteg || '0'}</Text></View>
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>{totalRow.consumo_inset || '0'}</Text></View>
            <View style={[s.cell, s.colSmall]}><Text style={s.cellTextBold}>{totalRow.n_triat || '0'}</Text></View>
            <View style={[s.cell, s.colSmall, s.cellLast]} />
          </View>
        </View>

        <View style={s.rodapeSection}>
          <View style={s.rodapeRow}>
            <View style={s.rodapeItem}>
              <Text style={s.cabecalhoLabel}>Rendimento / Homem / Dia</Text>
              <Text style={s.cabecalhoValue}>{rodape.rendimento || '-'}</Text>
            </View>
            <View style={s.rodapeItem}>
              <Text style={s.cabecalhoLabel}>Saldo de BHC ou K-O</Text>
              <Text style={s.cabecalhoValue}>{rodape.saldo_bhc || '-'}</Text>
            </View>
            <View style={s.rodapeItem}>
              <Text style={s.cabecalhoLabel}>Data de Assinatura</Text>
              <Text style={s.cabecalhoValue}>{formatDate(rodape.data_ass)}</Text>
            </View>
          </View>
          
          <View style={[s.rodapeRow, { marginTop: 10 }]}>
            <View style={s.rodapeItem}>
              <View style={s.assinaturaLine}><Text style={{ fontSize: 7 }}>Ass. Guarda Chefe</Text></View>
              <Text style={{ textAlign: 'center', fontSize: 7, marginTop: 2 }}>{rodape.ass_guarda || ''}</Text>
            </View>
            <View style={s.rodapeItem}>
              <View style={s.assinaturaLine}><Text style={{ fontSize: 7 }}>Visto Inspetor</Text></View>
              <Text style={{ textAlign: 'center', fontSize: 7, marginTop: 2 }}>{rodape.ass_inspetor || ''}</Text>
            </View>
          </View>
        </View>

        <View style={s.footer}>
          <Text>Vector Tracker - Sistema de Vigilância Entomológica</Text>
          <Text>Gerado em: {new Date().toLocaleString('pt-BR')}</Text>
        </View>
      </Page>
    </Document>
  );
};

export const BotaoDownloadBorrifacaoPDF = ({ data, cabecalho, rodape, totalRow, className }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    urlParaBase64(logoUrl).then(setLogoBase64);
  }, []);

  const handleDownload = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const doc = (
        <FormularioBorrifacaoPDFDocument 
          data={data} 
          cabecalho={cabecalho} 
          rodape={rodape}
          totalRow={totalRow}
          logoBase64={logoBase64}
        />
      );
      
      const blob = await pdf(doc).toBlob();
      const nomeArquivo = `Resumo_Borrifacao_Semana_${cabecalho.semana || 'nao_informada'}.pdf`;
      saveAs(blob, nomeArquivo);
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      alert("Erro ao gerar o PDF. Tente novamente.");
    } finally {
      setIsGenerating(false);
    }
  };

  const btnStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 20px',
    background: '#111827',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: isGenerating ? 'wait' : 'pointer',
    transition: 'background 0.15s',
    height: '38px',
    fontFamily: 'inherit',
    opacity: isGenerating ? 0.7 : 1
  };

  return (
    <button 
      className={className} 
      style={className ? {} : btnStyles}
      onClick={handleDownload} 
      disabled={isGenerating}
      title="Baixar Relatório PDF"
    >
      <DownloadIcon />
      {isGenerating ? 'Gerando...' : 'Baixar PDF'}
    </button>
  );
};

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
